import type { AuthConfig } from '@skhub/contracts';
import type { PublicClientApplication } from '@azure/msal-browser';

/**
 * Microsoft Entra sign-in in the browser (SPA flow: authorization code + PKCE, no client secret).
 * MSAL.js only proves who the user is; the API verifies the ID token and issues its own
 * HttpOnly session cookie. No Microsoft token is kept after that exchange.
 */

type EntraConfig = NonNullable<AuthConfig['entra']>;

/**
 * The site origin, which is what the app registration lists as its SPA redirect URI (e.g. http://localhost:5173,
 * no path). Microsoft returns to "/"; main.tsx hands the response on to /login before the router starts.
 */
export const entraRedirectUri = () => window.location.origin;

let instance: Promise<PublicClientApplication> | null = null;

function msal(cfg: EntraConfig) {
  instance ??= import('@azure/msal-browser').then(async ({ PublicClientApplication }) => {
    const app = new PublicClientApplication({
      auth: {
        clientId: cfg.clientId,
        authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
        redirectUri: entraRedirectUri(),
      },
      // Session storage: MSAL's cache dies with the tab and is cleared after the exchange anyway.
      cache: { cacheLocation: 'sessionStorage' },
    });
    await app.initialize();
    return app;
  });
  // A failed load (network blip) must not stay cached: the next attempt starts again.
  instance.catch(() => {
    instance = null;
  });
  return instance;
}

const RETURN_KEY = 'skhub.entra.returnTo';

/** True while this page load is Microsoft sending the user back (the response is in the URL). */
export const isEntraReturn = () => /[?#&](code|error)=/.test(window.location.hash + window.location.search);

/** The page the user was going to before sign-in, kept across the round trip to Microsoft (read once). */
export function takeEntraReturnTo(): string | null {
  try {
    const v = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return v;
  } catch {
    return null;
  }
}

/** Sends the browser to Microsoft. Resolves only if navigation fails. */
export async function startEntraSignIn(cfg: EntraConfig, returnTo?: string | null, loginHint?: string) {
  try {
    if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo);
  } catch {
    /* private mode: the user lands on their home page instead */
  }
  const app = await msal(cfg);
  // With an email the user typed, Microsoft goes straight to that account; without one, it offers the account picker.
  await app.loginRedirect({ scopes: cfg.scopes, redirectUri: entraRedirectUri(), ...(loginHint ? { loginHint } : { prompt: 'select_account' }) });
}

/**
 * On return from Microsoft, completes the code exchange and returns the ID token (null when this
 * page load is not a sign-in return). MSAL's own cache is cleared straight after.
 */
export async function finishEntraSignIn(cfg: EntraConfig): Promise<string | null> {
  const app = await msal(cfg);
  const result = await app.handleRedirectPromise();
  if (!result?.idToken) return null;
  try {
    await app.clearCache();
  } catch {
    /* best effort: the cache is session-scoped anyway */
  }
  return result.idToken;
}
